import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';

const ProductForm = () => {
  const navigate = useNavigate();
  const { productId } = useParams();
  const isEditing = !!productId;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('orders');
  const [categories, setCategories] = useState([]);
  
  const [formData, setFormData] = useState({
    // Basic Information
    name: '',
    sku: '',
    description: '',
    category_id: '', // FK to product_categories
    subcategory: '',
    brand: '',
    
    // Pricing
    price: '',
    base_price: '',
    cost_price: '',
    sale_price: '',
    currency: 'USD',
    
    // Physical Attributes
    weight: '',
    dimensions: { length: '', width: '', height: '', unit: 'cm' },
    
    // Inventory
    status: 'active',
    is_active: true,
    is_featured: false,
    is_bestseller: false,
    is_new: false,
    inventory_count: '',
    stock_quantity: '',
    low_stock_threshold: '10',
    
    // SEO
    meta_title: '',
    meta_description: '',
    url: '',
    
    // Metadata
    tags: [],
    attributes: {},
    images: []
  });

  const [orders, setOrders] = useState([]);
  const [metrics, setMetrics] = useState(null);

  // Load categories on mount
  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await api.get('/ecommerce/product-categories');
      setCategories(response.data.categories || []);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  };

  useEffect(() => {
    if (isEditing) {
      fetchProduct();
      fetchOrders();
      fetchMetrics();
    }
  }, [productId]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/ecommerce/products/${productId}`);
      const product = response.data;
      setFormData({
        name: product.name || '',
        sku: product.sku || '',
        description: product.description || '',
        category_id: product.category_id || '', // FK to product_categories
        subcategory: product.subcategory || '',
        brand: product.brand || '',
        price: product.price || '',
        base_price: product.base_price || '',
        cost_price: product.cost_price || '',
        sale_price: product.sale_price || '',
        currency: product.currency || 'USD',
        weight: product.weight || '',
        dimensions: product.dimensions || { length: '', width: '', height: '', unit: 'cm' },
        status: product.status || 'active',
        is_active: product.is_active !== undefined ? product.is_active : true,
        is_featured: product.is_featured || false,
        is_bestseller: product.is_bestseller || false,
        is_new: product.is_new || false,
        inventory_count: product.inventory_count || '',
        stock_quantity: product.stock_quantity || '',
        low_stock_threshold: product.low_stock_threshold || '10',
        meta_title: product.meta_title || '',
        meta_description: product.meta_description || '',
        url: product.url || '',
        tags: product.tags || [],
        attributes: product.attributes || {},
        images: product.images || []
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load product');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      const response = await api.get(`/ecommerce/products/${productId}/orders`);
      setOrders(response.data.orders || []);
    } catch (err) {
      console.error('Failed to fetch orders:', err);
    }
  };

  const fetchMetrics = async () => {
    try {
      const response = await api.get(`/ecommerce/products/${productId}/metrics`);
      setMetrics(response.data);
    } catch (err) {
      console.error('Failed to fetch metrics:', err);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleDimensionChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      dimensions: { ...prev.dimensions, [field]: value }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    
    try {
      // Clean data - convert empty strings to null
      const cleanedData = { ...formData };
      Object.keys(cleanedData).forEach(key => {
        if (cleanedData[key] === '') {
          cleanedData[key] = null;
        }
      });

      if (isEditing) {
        await api.put(`/ecommerce/products/${productId}`, cleanedData);
        setSuccess('Product updated successfully');
      } else {
        const response = await api.post('/ecommerce/products', cleanedData);
        setSuccess('Product created successfully');
        navigate(`/products/${response.data.product.id}/edit`);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save product');
    }
  };

  if (loading && isEditing) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumb */}
      <nav className="breadcrumb">
        <button onClick={() => navigate('/dashboard')} className="breadcrumb-link">Dashboard</button>
        <span className="breadcrumb-separator">/</span>
        <button onClick={() => navigate('/products')} className="breadcrumb-link">Products</button>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-current">{isEditing ? 'Edit Product' : 'New Product'}</span>
      </nav>

      {/* Header with Actions */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.875rem', fontWeight: 'bold', color: 'var(--gray-900)', margin: 0 }}>
            {isEditing ? '🛍️ Edit Product' : '🛍️ New Product'}
          </h1>
          <p style={{ color: 'var(--gray-600)', margin: '0.25rem 0 0 0', fontSize: '0.875rem' }}>
            {isEditing ? 'Update product information and details' : 'Create a new product in your catalog'}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button onClick={() => navigate('/products')} className="btn btn-outline">
            ← Back
          </button>
          <button onClick={handleSubmit} className="btn btn-primary">
            💾 {isEditing ? 'Update' : 'Create'} Product
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div style={{ backgroundColor: 'var(--red-50)', border: '1px solid var(--red-200)', color: 'var(--red-800)', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem', cursor: 'pointer' }} onClick={() => setError(null)}>
          {error}
        </div>
      )}

      {success && (
        <div style={{ backgroundColor: 'var(--success-50)', border: '1px solid var(--success-500)', color: 'var(--success-700)', padding: '0.75rem', borderRadius: '8px', marginBottom: '1rem', cursor: 'pointer' }} onClick={() => setSuccess(null)}>
          {success}
        </div>
      )}

      {/* Main Form Card */}
      <div className="card">
        <form onSubmit={handleSubmit}>
          {/* Basic Information */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid var(--gray-200)' }}>
              📝 Basic Information
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                  SKU *
                </label>
                <input
                  type="text"
                  required
                  value={formData.sku}
                  onChange={(e) => handleChange('sku', e.target.value)}
                  placeholder="Product SKU"
                  className="form-input"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                  Product Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Enter product name"
                  className="form-input"
                />
              </div>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                Description
              </label>
              <textarea
                rows="4"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Product description"
                className="form-input"
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                  Category
                </label>
                <select
                  value={formData.category_id}
                  onChange={(e) => handleChange('category_id', e.target.value)}
                  className="form-input"
                >
                  <option value="">Select category...</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.level > 0 ? '└─ '.repeat(category.level) : ''}{category.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                  Subcategory
                </label>
                <input
                  type="text"
                  value={formData.subcategory}
                  onChange={(e) => handleChange('subcategory', e.target.value)}
                  placeholder="Product subcategory"
                  className="form-input"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                  Brand
                </label>
                <input
                  type="text"
                  value={formData.brand}
                  onChange={(e) => handleChange('brand', e.target.value)}
                  placeholder="Product brand"
                  className="form-input"
                />
              </div>
            </div>
          </div>

          {/* Pricing */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid var(--gray-200)' }}>
              💰 Pricing
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                  Price *
                </label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={formData.price}
                  onChange={(e) => handleChange('price', e.target.value)}
                  placeholder="0.00"
                  className="form-input"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                  Base Price
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.base_price}
                  onChange={(e) => handleChange('base_price', e.target.value)}
                  placeholder="0.00"
                  className="form-input"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                  Cost Price
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.cost_price}
                  onChange={(e) => handleChange('cost_price', e.target.value)}
                  placeholder="0.00"
                  className="form-input"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                  Sale Price
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.sale_price}
                  onChange={(e) => handleChange('sale_price', e.target.value)}
                  placeholder="0.00"
                  className="form-input"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                  Currency
                </label>
                <select
                  value={formData.currency}
                  onChange={(e) => handleChange('currency', e.target.value)}
                  className="form-input"
                >
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>
            </div>
          </div>

          {/* Physical Attributes */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid var(--gray-200)' }}>
              📦 Physical Attributes
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                  Weight
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.weight}
                  onChange={(e) => handleChange('weight', e.target.value)}
                  placeholder="0.00"
                  className="form-input"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                  Length
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.dimensions.length}
                  onChange={(e) => handleDimensionChange('length', e.target.value)}
                  placeholder="0.00"
                  className="form-input"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                  Width
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.dimensions.width}
                  onChange={(e) => handleDimensionChange('width', e.target.value)}
                  placeholder="0.00"
                  className="form-input"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                  Height
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.dimensions.height}
                  onChange={(e) => handleDimensionChange('height', e.target.value)}
                  placeholder="0.00"
                  className="form-input"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                  Unit
                </label>
                <select
                  value={formData.dimensions.unit}
                  onChange={(e) => handleDimensionChange('unit', e.target.value)}
                  className="form-input"
                >
                  <option value="cm">cm</option>
                  <option value="in">in</option>
                  <option value="m">m</option>
                </select>
              </div>
            </div>
          </div>

          {/* Inventory & Status */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid var(--gray-200)' }}>
              📊 Inventory & Status
            </h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                  Inventory Count
                </label>
                <input
                  type="number"
                  value={formData.inventory_count}
                  onChange={(e) => handleChange('inventory_count', e.target.value)}
                  placeholder="0"
                  className="form-input"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                  Stock Quantity
                </label>
                <input
                  type="number"
                  value={formData.stock_quantity}
                  onChange={(e) => handleChange('stock_quantity', e.target.value)}
                  placeholder="0"
                  className="form-input"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                  Low Stock Threshold
                </label>
                <input
                  type="number"
                  value={formData.low_stock_threshold}
                  onChange={(e) => handleChange('low_stock_threshold', e.target.value)}
                  placeholder="10"
                  className="form-input"
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => handleChange('status', e.target.value)}
                  className="form-input"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="discontinued">Discontinued</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => handleChange('is_active', e.target.checked)}
                />
                <span style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>Active</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.is_featured}
                  onChange={(e) => handleChange('is_featured', e.target.checked)}
                />
                <span style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>Featured</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.is_bestseller}
                  onChange={(e) => handleChange('is_bestseller', e.target.checked)}
                />
                <span style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>Bestseller</span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={formData.is_new}
                  onChange={(e) => handleChange('is_new', e.target.checked)}
                />
                <span style={{ fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)' }}>New</span>
              </label>
            </div>
          </div>

          {/* SEO */}
          <div style={{ marginBottom: '2rem' }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '2px solid var(--gray-200)' }}>
              🔍 SEO & URL
            </h3>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                Product URL
              </label>
              <input
                type="url"
                value={formData.url}
                onChange={(e) => handleChange('url', e.target.value)}
                placeholder="https://example.com/product"
                className="form-input"
              />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                Meta Title
              </label>
              <input
                type="text"
                value={formData.meta_title}
                onChange={(e) => handleChange('meta_title', e.target.value)}
                placeholder="SEO title for search engines"
                className="form-input"
              />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', color: 'var(--gray-700)', marginBottom: '0.5rem' }}>
                Meta Description
              </label>
              <textarea
                rows="2"
                value={formData.meta_description}
                onChange={(e) => handleChange('meta_description', e.target.value)}
                placeholder="SEO description for search engines"
                className="form-input"
              />
            </div>
          </div>
        </form>
      </div>

      {/* Tabs - Only show when editing */}
      {isEditing && (
        <div style={{ marginTop: '1.5rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', borderBottom: '2px solid var(--gray-200)', marginBottom: '1.5rem' }}>
            <button
              onClick={() => setActiveTab('orders')}
              className={`btn btn-sm ${activeTab === 'orders' ? 'btn-primary' : 'btn-outline'}`}
              style={{ borderRadius: '6px 6px 0 0', border: 'none', borderBottom: activeTab === 'orders' ? '3px solid #EF4444' : 'none' }}
            >
              📦 Orders
            </button>
            <button
              onClick={() => setActiveTab('metrics')}
              className={`btn btn-sm ${activeTab === 'metrics' ? 'btn-primary' : 'btn-outline'}`}
              style={{ borderRadius: '6px 6px 0 0', border: 'none', borderBottom: activeTab === 'metrics' ? '3px solid #EF4444' : 'none' }}
            >
              📈 Metrics
            </button>
          </div>

          {/* Orders Tab */}
          {activeTab === 'orders' && (
            <div className="card">
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '1rem' }}>
                📦 Recent Orders
              </h3>
              {orders.length === 0 ? (
                <p style={{ color: 'var(--gray-500)', textAlign: 'center', padding: '2rem' }}>
                  No orders found for this product
                </p>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: 'var(--gray-50)', borderBottom: '1px solid var(--gray-200)' }}>
                      <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem' }}>Order ID</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem' }}>Customer</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem' }}>Quantity</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem' }}>Total</th>
                      <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.875rem' }}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((order) => (
                      <tr key={order.id} style={{ borderBottom: '1px solid var(--gray-200)' }}>
                        <td style={{ padding: '0.75rem' }}>#{order.id}</td>
                        <td style={{ padding: '0.75rem' }}>{order.customer_email || '-'}</td>
                        <td style={{ padding: '0.75rem' }}>{order.quantity}</td>
                        <td style={{ padding: '0.75rem' }}>${parseFloat(order.total || 0).toFixed(2)}</td>
                        <td style={{ padding: '0.75rem' }}>
                          {order.created_at ? new Date(order.created_at).toLocaleDateString() : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Metrics Tab */}
          {activeTab === 'metrics' && (
            <div className="card">
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: 'var(--gray-900)', marginBottom: '1rem' }}>
                📈 Product Metrics
              </h3>
              {metrics ? (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                  <div style={{ padding: '1rem', backgroundColor: 'var(--gray-50)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Total Views</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', marginTop: '0.25rem' }}>{metrics.total_views || 0}</div>
                  </div>
                  <div style={{ padding: '1rem', backgroundColor: 'var(--gray-50)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Total Sales</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', marginTop: '0.25rem' }}>{metrics.total_sales || 0}</div>
                  </div>
                  <div style={{ padding: '1rem', backgroundColor: 'var(--gray-50)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Revenue</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', marginTop: '0.25rem' }}>${(metrics.total_revenue || 0).toLocaleString()}</div>
                  </div>
                  <div style={{ padding: '1rem', backgroundColor: 'var(--gray-50)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '0.875rem', color: 'var(--gray-600)' }}>Avg Rating</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 'bold', marginTop: '0.25rem' }}>{metrics.avg_rating ? metrics.avg_rating.toFixed(1) : 'N/A'}</div>
                  </div>
                </div>
              ) : (
                <p style={{ color: 'var(--gray-500)', textAlign: 'center', padding: '2rem' }}>
                  No metrics data available
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductForm;
